import { useEffect, useState, useRef } from "react";
import { api, formatApiError } from "../lib/api";
import {
  X, Camera, Trash2, CheckCircle2, AlertTriangle, ClipboardCheck, Lock, Edit3, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

const RATINGS = [
  { v: "ok",           l: "OK",            color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  { v: "minor_issue",  l: "Minor",         color: "bg-amber-50 text-amber-800 border-amber-200" },
  { v: "damage",       l: "Damage",        color: "bg-red-50 text-red-800 border-red-200" },
  { v: "missing",      l: "Missing",       color: "bg-slate-100 text-slate-800 border-slate-300" },
  { v: "na",           l: "N/A",           color: "bg-slate-50 text-slate-500 border-slate-200" },
];

const TYPE_LABEL = { pickup: "Pickup inspection", return: "Return inspection" };

/**
 * Inspection sheet: full-screen drawer for capturing pickup/return condition.
 *
 * Props:
 *   bookingId, type ("pickup" | "return"), open, onClose, onDone
 */
export default function InspectionSheet({ bookingId, type, open, onClose, onDone, preloaded = null }) {
  const [inspection, setInspection] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [defaultChecklist, setDefaultChecklist] = useState([]);
  const [templateInfo, setTemplateInfo] = useState(null);
  const [mileageConflict, setMileageConflict] = useState(null); // { currentKm, submittedKm, reason } | null
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (preloaded) {
      setInspection(structuredClone(preloaded.inspection || preloaded));
      setPhotos(structuredClone(preloaded.photos || []));
      setDefaultChecklist(structuredClone(preloaded.default_checklist || preloaded.inspection?.checklist || preloaded.checklist || []));
      setTemplateInfo(preloaded.template || null);
      setLoading(false);
      setMileageConflict(null);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/bookings/${bookingId}/inspections`);
        setDefaultChecklist(data.default_checklist || []);
        setTemplateInfo(data.templates?.[type] || null);
        const existing = data.by_type?.[type];
        if (existing) {
          await hydrate(existing.id);
        } else {
          // create draft
          const { data: created } = await api.post(
            `/bookings/${bookingId}/inspections`,
            { inspection_type: type });
          await hydrate(created.id);
        }
      } catch (e) {
        toast.error(formatApiError(e));
        onClose?.();
      } finally {
        setLoading(false);
      }
    })();
    setMileageConflict(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bookingId, type, preloaded]);

  const hydrate = async (id) => {
    const { data } = await api.get(`/inspections/${id}`);
    setInspection(data.inspection);
    setPhotos(data.photos || []);
  };

  const finalized = inspection?.status === "finalized";

  const updateChecklistItem = (key, field, value) => {
    setInspection((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        checklist: (prev.checklist || []).map((c) =>
          c.key === key ? { ...c, [field]: value } : c),
      };
    });
  };

  const save = async (extra = {}, silent = false) => {
    if (!inspection || finalized) return;
    if (preloaded) {
      const updated = { ...inspection, ...extra, updated_at: new Date().toISOString() };
      setInspection(updated);
      if (!silent) onDone?.(updated);
      return updated;
    }
    setSaving(true);
    try {
      const body = {
        fuel_level: inspection.fuel_level,
        odometer_km: inspection.odometer_km,
        exterior_notes: inspection.exterior_notes,
        interior_notes: inspection.interior_notes,
        summary_note: inspection.summary_note,
        customer_signature_name: inspection.customer_signature_name,
        customer_acknowledged: !!inspection.customer_acknowledged,
        checklist: inspection.checklist,
        cleaning_condition: inspection.cleaning_condition,
        ...extra,
      };
      const { data } = await api.put(`/inspections/${inspection.id}`, body);
      setInspection(data);
      // `silent` skips the toast + onDone() refresh: used by finalize()'s
      // pre-persist step, where onDone() triggers the parent's loading-gate
      // (BookingDetail.jsx unmounts on `loading`), which was wiping out
      // mileageConflict before the correction UI could render.
      if (!silent) {
        toast.success("Saved");
        onDone?.();
      }
      return data;
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onPhotoPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !inspection) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Photo too large: max 4MB.");
      return;
    }
    setSaving(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(file);
      });
      if (preloaded) {
        const demoPhoto = { id: `demo-photo-${Date.now()}`, data_url: dataUrl, name: file.name };
        setPhotos((prev) => [...prev, demoPhoto]);
        setInspection((prev) => ({ ...prev, photo_count: (prev?.photo_count || 0) + 1 }));
        return;
      }
      const base64 = dataUrl.split(",")[1];
      const { data: created } = await api.post(
        `/inspections/${inspection.id}/photos`,
        {
          name: file.name, content_type: file.type || "image/jpeg",
          data_base64: base64,
        });
      // Fetch with data URL for preview
      const { data: full } = await api.get(
        `/inspections/${inspection.id}/photos/${created.id}`);
      setPhotos((prev) => [...prev, full]);
      setInspection((prev) => ({ ...prev, photo_count: (prev?.photo_count || 0) + 1 }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const deletePhoto = async (pid) => {
    if (preloaded) {
      setPhotos((prev) => prev.filter((p) => p.id !== pid));
      setInspection((prev) => ({ ...prev, photo_count: Math.max(0, (prev?.photo_count || 1) - 1) }));
      return;
    }
    try {
      await api.delete(`/inspections/${inspection.id}/photos/${pid}`);
      setPhotos((prev) => prev.filter((p) => p.id !== pid));
      setInspection((prev) => ({ ...prev, photo_count: Math.max(0, (prev?.photo_count || 1) - 1) }));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const finalize = async (overrideReason = null) => {
    if (!inspection.customer_acknowledged || !inspection.customer_signature_name?.trim()) {
      toast.error("Customer signature & acknowledgement required to finalize.");
      return;
    }
    if (!overrideReason) await save({}, true);
    try {
      if (preloaded) {
        const finalizedInspection = { ...inspection, status: "finalized", finalized_at: new Date().toISOString() };
        setInspection(finalizedInspection);
        setMileageConflict(null);
        onDone?.(finalizedInspection);
        return;
      }
      const body = overrideReason
        ? { mileage_override: true, mileage_override_reason: overrideReason }
        : {};
      const { data } = await api.post(`/inspections/${inspection.id}/finalize`, body);
      setInspection(data);
      setMileageConflict(null);
      toast.success("Inspection finalized");
      onDone?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "mileage_below_current") {
        setMileageConflict({
          currentKm: detail.current_mileage_km,
          submittedKm: detail.submitted_mileage_km,
          reason: "",
        });
      } else {
        toast.error(formatApiError(e));
      }
    }
  };

  return open ? (
    <div
      data-testid={`inspection-sheet-${type}`}
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-6"
    >
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-xl max-h-[95vh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold inline-flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-[#1B4332]" />
            {TYPE_LABEL[type] || "Inspection"}
            {finalized && (
              <span className="ml-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">
                <Lock className="w-3 h-3" /> Finalized
              </span>
            )}
          </h2>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-slate-100"
                  data-testid="inspection-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {loading && <div className="text-sm text-slate-500">Loading…</div>}
          {inspection && (
            <>
              {/* Quick fields */}
              <div className={`grid sm:grid-cols-2 ${type === "return" ? "lg:grid-cols-3" : ""} gap-3`}>
                <Field
                  label="Fuel level (%)"
                  testId="insp-fuel"
                  type="number"
                  value={inspection.fuel_level ?? ""}
                  disabled={finalized}
                  onChange={(v) => setInspection((prev) => prev ? ({ ...prev, ...{ fuel_level: v === "" ? null : Math.min(100, Math.max(0, Number(v))) } }) : prev)}
                />
                <Field
                  label="Odometer (km)"
                  testId="insp-odometer"
                  type="number"
                  value={inspection.odometer_km ?? ""}
                  disabled={finalized}
                  onChange={(v) => {
                    setMileageConflict(null);
                    setInspection((prev) => prev ? ({ ...prev, ...{ odometer_km: v === "" ? null : Math.max(0, Number(v)) } }) : prev);
                  }}
                />
                {type === "return" && (
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Cleaning condition</span>
                    <select
                      disabled={finalized}
                      data-testid="insp-cleaning-condition"
                      value={inspection.cleaning_condition ?? ""}
                      onChange={(e) => setInspection((prev) => prev ? ({ ...prev, cleaning_condition: e.target.value || null }) : prev)}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100"
                    >
                      <option value="">Not recorded</option>
                      <option value="clean">Clean</option>
                      <option value="needs_cleaning">Needs cleaning</option>
                      <option value="heavily_soiled">Heavily soiled</option>
                    </select>
                  </label>
                )}
              </div>

              {/* Checklist */}
              <div>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <h3 className="font-display text-base font-semibold">Condition checklist</h3>
                  {templateInfo?.name && (
                    <span
                      className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200"
                      data-testid="inspection-template-badge"
                      title={`Template id: ${templateInfo.id || "-"}`}
                    >
                      Using {templateInfo.name}
                    </span>
                  )}
                </div>
                <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                  {(inspection.checklist || defaultChecklist).map((c) => (
                    <div key={c.key} className="p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3"
                         data-testid={`insp-row-${c.key}`}>
                      <div className="sm:w-44 font-medium text-sm">{c.label || c.key}</div>
                      <div className="flex flex-wrap gap-1">
                        {RATINGS.map((r) => (
                          <button
                            key={r.v}
                            type="button"
                            disabled={finalized}
                            onClick={() => updateChecklistItem(c.key, "rating", r.v)}
                            data-testid={`insp-${c.key}-${r.v}`}
                            className={`text-xs px-2 py-1 rounded-md border ${
                              c.rating === r.v ? r.color + " ring-2 ring-offset-1 ring-current/30" : "bg-white border-slate-200 hover:bg-slate-50 text-slate-600"
                            }`}
                          >{r.l}</button>
                        ))}
                      </div>
                      <input
                        type="text"
                        disabled={finalized}
                        placeholder="Note (optional)"
                        value={c.note || ""}
                        onChange={(e) => updateChecklistItem(c.key, "note", e.target.value)}
                        className="flex-1 min-w-0 text-sm rounded-md border border-slate-300 px-2 py-1.5 disabled:bg-slate-50"
                        data-testid={`insp-${c.key}-note`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div className="grid sm:grid-cols-2 gap-3">
                <TextareaField label="Exterior notes" disabled={finalized}
                  value={inspection.exterior_notes || ""}
                  testId="insp-exterior-notes"
                  onChange={(v) => setInspection((prev) => prev ? ({ ...prev, ...{ exterior_notes: v } }) : prev)} />
                <TextareaField label="Interior notes" disabled={finalized}
                  value={inspection.interior_notes || ""}
                  testId="insp-interior-notes"
                  onChange={(v) => setInspection((prev) => prev ? ({ ...prev, ...{ interior_notes: v } }) : prev)} />
              </div>
              <TextareaField label="Summary note (visible internally)" disabled={finalized}
                value={inspection.summary_note || ""}
                testId="insp-summary-note"
                onChange={(v) => setInspection((prev) => prev ? ({ ...prev, ...{ summary_note: v } }) : prev)} />

              {/* Photos */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-display text-base font-semibold">Photos ({photos.length})</h3>
                  {!finalized && (
                    <>
                      <input type="file" accept="image/*" ref={fileRef}
                             onChange={onPhotoPicked} hidden
                             data-testid="insp-photo-input" />
                      <button
                        onClick={() => fileRef.current?.click()}
                        data-testid="insp-add-photo"
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm"
                      >
                        <Camera className="w-4 h-4" /> Add photo
                      </button>
                    </>
                  )}
                </div>
                {photos.length === 0 ? (
                  <div className="text-sm text-slate-500 italic">No photos yet.</div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                    {photos.map((p) => (
                      <div key={p.id} className="relative group rounded-lg overflow-hidden border border-slate-200 bg-slate-50"
                           data-testid={`insp-photo-${p.id}`}>
                        <img
                          src={p.data_url || `${api.defaults?.baseURL || ""}/inspections/${inspection.id}/photos/${p.id}`}
                          alt={p.caption || p.name}
                          className="w-full h-32 object-cover" />
                        {p.caption && <div className="text-[11px] p-1.5 text-slate-600 line-clamp-2">{p.caption}</div>}
                        {!finalized && (
                          <button
                            onClick={() => deletePhoto(p.id)}
                            data-testid={`insp-photo-delete-${p.id}`}
                            className="absolute top-1 right-1 p-1 rounded-md bg-white/90 hover:bg-white text-red-600 opacity-0 group-hover:opacity-100 transition"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Mileage conflict: decreasing odometer needs an explicit correction reason */}
              {mileageConflict && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-3" data-testid="insp-mileage-conflict">
                  <div className="flex items-start gap-3">
                    <ShieldAlert className="w-5 h-5 text-amber-700 mt-0.5" />
                    <div>
                      <div className="font-semibold text-amber-900">
                        Submitted mileage ({mileageConflict.submittedKm.toLocaleString()} km) is lower than the vehicle&apos;s current recorded mileage ({mileageConflict.currentKm.toLocaleString()} km)
                      </div>
                      <p className="text-sm text-amber-900/80 mt-0.5">
                        Confirm this is a correction and provide a reason to proceed.
                      </p>
                    </div>
                  </div>
                  <textarea rows={2} value={mileageConflict.reason}
                            onChange={(e) => setMileageConflict((p) => p && ({ ...p, reason: e.target.value }))}
                            placeholder="e.g. Odometer was replaced: new unit reads correctly from the last service."
                            data-testid="insp-mileage-conflict-reason"
                            className="w-full text-sm rounded-md border border-slate-300 p-2" />
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setMileageConflict(null)}
                            className="px-3 py-1.5 text-sm rounded-md hover:bg-amber-100">Cancel</button>
                    <button onClick={() => mileageConflict.reason.trim() && finalize(mileageConflict.reason.trim())}
                            disabled={!mileageConflict.reason.trim()}
                            data-testid="insp-mileage-conflict-confirm"
                            className="px-3 py-1.5 text-sm rounded-md bg-amber-600 text-white hover:opacity-90 disabled:opacity-50">
                      Finalize as correction
                    </button>
                  </div>
                </div>
              )}

              {/* Signature & acknowledgement */}
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                <h3 className="font-display text-base font-semibold mb-2">Customer acknowledgement</h3>
                <p className="text-xs text-slate-600 mb-2">
                  Type the customer&apos;s full name as a typed signature. They acknowledge they reviewed the condition above.
                </p>
                <input
                  type="text"
                  disabled={finalized}
                  value={inspection.customer_signature_name || ""}
                  onChange={(e) => setInspection((prev) => prev ? ({ ...prev, ...{ customer_signature_name: e.target.value } }) : prev)}
                  placeholder="Customer's typed name"
                  data-testid="insp-signature"
                  className="w-full text-sm rounded-md border border-slate-300 px-3 py-2 disabled:bg-slate-100"
                />
                <label className="flex items-center gap-2 text-sm mt-2">
                  <input type="checkbox" disabled={finalized}
                         checked={!!inspection.customer_acknowledged}
                         onChange={(e) => setInspection((prev) => prev ? ({ ...prev, ...{ customer_acknowledged: e.target.checked } }) : prev)}
                         data-testid="insp-ack" />
                  <span>Customer has reviewed the inspection and acknowledges its accuracy.</span>
                </label>
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2 flex-wrap">
          {!finalized && (
            <>
              <button onClick={onClose} data-testid="insp-cancel"
                      className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm">
                Close
              </button>
              <button onClick={() => save()} disabled={saving} data-testid="insp-save"
                      className="px-4 py-2 rounded-lg border border-[#1B4332] bg-white text-[#1B4332] hover:bg-emerald-50 text-sm inline-flex items-center gap-1 disabled:opacity-50">
                <Edit3 className="w-4 h-4" /> {saving ? "Saving…" : "Save draft"}
              </button>
              <button onClick={() => finalize()} disabled={saving || !!mileageConflict}
                      data-testid="insp-finalize"
                      className="px-4 py-2 rounded-lg bg-[#1B4332] text-white hover:opacity-90 text-sm inline-flex items-center gap-1 disabled:opacity-50">
                <CheckCircle2 className="w-4 h-4" /> Finalize
              </button>
            </>
          )}
          {finalized && (
            <button onClick={onClose} data-testid="insp-close-final"
                    className="px-4 py-2 rounded-lg bg-[#1B4332] text-white text-sm">
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  ) : null;
}

function Field({ label, value, onChange, type = "text", disabled, testId }) {

  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      <input
        type={type} disabled={disabled} value={value}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100"
      />
    </label>
  );
}

function TextareaField({ label, value, onChange, disabled, testId }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      <textarea
        rows={3} disabled={disabled} value={value}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100"
      />
    </label>
  );
}
